import { ActionButton } from '../concept/action-button';

export interface AttentionItem {
  id: string;
  name: string;
  initials: string;
  headline: string;
  detail: string;
  actionLabel: string;
  /** The one row whose action is the conversation that cannot wait. */
  primary?: boolean;
}

/**
 * A row of the "Needs your attention" queue.
 *
 * Deliberately one line of why and one line of evidence. The deck's principle
 * slide gives the coach ten seconds for the whole screen, which is not enough
 * to read a table.
 */
export const AttentionRow: React.FC<{
  item: AttentionItem;
  onAction: () => void;
  onOpen: () => void;
  last?: boolean;
}> = ({ item, onAction, onOpen, last }) => (
  <div
    data-testid={`attention-${item.id}`}
    className={`flex items-center gap-3.5 py-3 ${last ? '' : 'border-b border-divider'}`}
  >
    <button
      type="button"
      onClick={onOpen}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-[11px] font-semibold text-accent-foreground"
      aria-label={`Open ${item.name}`}
    >
      {item.initials || '—'}
    </button>
    {/* Name and reason share a line where there is room for both, and stack
        on a phone, where the reason was being cut to "New sc…". */}
    <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
      <span className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2.5">
        <span className="truncate text-sm font-semibold sm:text-[13px]">{item.name}</span>
        <span className="truncate text-xs text-foreground-soft">{item.headline}</span>
      </span>
      <span className="tabular mt-0.5 block truncate text-[11px] text-muted-foreground">
        {item.detail}
      </span>
    </button>
    <ActionButton
      onClick={onAction}
      testId={`action-${item.id}`}
      ariaLabel={`${item.actionLabel}: ${item.name}`}
      className="shrink-0 whitespace-nowrap max-sm:min-h-11"
      variant={item.primary ? 'primary' : 'secondary'}
    >
      {item.actionLabel}
    </ActionButton>
  </div>
);
